import { createPortal } from 'react-dom';
import { IonButton, IonPopover, IonContent, IonList, IonItem, IonIcon } from '@ionic/react';
import { chevronDown, checkmark } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_META } from '../../i18n/languageMeta';
import { useLanguageSelection } from '../../hooks/useLanguageSelection';

interface HeaderLanguageButtonProps {
  /** Unique id used to wire the trigger button to its popover. Defaults to a
   *  stable value; pass a distinct id if two instances ever share a page. */
  triggerId?: string;
}

/**
 * Language selector sized for an Ionic toolbar/header slot. Unlike the dropdown
 * LanguageSwitcher — whose absolutely-positioned menu is clipped by the
 * toolbar's `overflow: hidden` — this opens an IonPopover, an Ionic overlay
 * that renders at the app root and therefore escapes the toolbar's clip. Both
 * controls share `useLanguageSelection` + `LANGUAGE_META`, so they never drift.
 *
 * The popover is portaled to <body>. When this button lives in a header inside
 * an IonModal, the modal wrapper's `transform` (translateZ(0) + desktop
 * translate(-50%,-50%)) becomes the containing block for the popover's
 * `position: fixed` host — so a popover left in the modal's DOM resolves its
 * coordinates against the small centered modal card and flies off-screen on
 * desktop (the iPhone-frame viewport). Portaling to <body> escapes that
 * transformed ancestor so the popover positions against the viewport. The
 * `trigger` wiring is by global id, so it still finds the button.
 *
 * Renders nothing when there are fewer than two supported languages. Meant to
 * be placed inside <IonButtons> in an <IonToolbar>.
 */
export const HeaderLanguageButton: React.FC<HeaderLanguageButtonProps> = ({
  triggerId = 'header-language-trigger',
}) => {
  const { t } = useTranslation('common');
  const { current, select, languages } = useLanguageSelection();

  if (languages.length < 2) return null;

  const currentMeta = LANGUAGE_META[current];

  return (
    <>
      <IonButton
        id={triggerId}
        fill="clear"
        aria-label={`${t('ui.languageAria')} (${currentMeta?.nativeName ?? current.toUpperCase()})`}
      >
        {currentMeta && (
          <img
            src={currentMeta.flag}
            alt=""
            aria-hidden="true"
            className="mr-1.5 h-3.5 w-5 rounded-sm object-cover"
          />
        )}
        <span className="text-sm font-semibold normal-case">
          {currentMeta?.nativeName ?? current.toUpperCase()}
        </span>
        <IonIcon icon={chevronDown} slot="end" className="text-xs opacity-60" />
      </IonButton>

      {createPortal(
        <IonPopover trigger={triggerId} side="bottom" alignment="start" dismissOnSelect>
          <IonContent>
            <IonList aria-label={t('ui.languageAria')}>
              {languages.map((code) => {
                const meta = LANGUAGE_META[code];
                const isCurrent = code === current;
                return (
                  <IonItem key={code} button detail={false} onClick={() => select(code)}>
                    {meta && (
                      <img
                        src={meta.flag}
                        alt=""
                        aria-hidden="true"
                        slot="start"
                        className="h-3.5 w-5 rounded-sm object-cover"
                      />
                    )}
                    <span className={`text-sm ${isCurrent ? 'font-semibold' : ''}`}>
                      {meta?.nativeName ?? code.toUpperCase()}
                    </span>
                    {isCurrent && (
                      <IonIcon
                        icon={checkmark}
                        slot="end"
                        className="text-brand-primary"
                        aria-hidden="true"
                      />
                    )}
                  </IonItem>
                );
              })}
            </IonList>
          </IonContent>
        </IonPopover>,
        document.body,
      )}
    </>
  );
};
